import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { INewsFeed, defaultValue } from 'app/shared/model/news-feed.model';

const initialState: EntityState<INewsFeed> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = '/api/admin/news-feeds';

// Actions

export const getEntities = createAsyncThunk('newsFeed/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<INewsFeed[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'newsFeed/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<INewsFeed>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);
export const getEntityNewsFeed = createAsyncThunk(
  'newsFeed/fetch_entity_newsfeed',
  async ({ id, page, size, sort }: { id: string | number; page: number; size: number; sort: string }) => {
    const requestUrl = `${apiUrl}/?userId.equals=${id}&page=${page}&size=${size}&sort=${sort}`;
    return axios.get<INewsFeed>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'newsFeed/create_entity',
  async (entity: INewsFeed, thunkAPI) => {
    const result = await axios.post<INewsFeed>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);
export const createEntitynewsFeed = createAsyncThunk(
  'newsFeed/create_entity_newsFeed',
  async (entity: INewsFeed, thunkAPI) => {
    try {
      const result = await axios.post<INewsFeed>(apiUrl, cleanEntity(entity));
      thunkAPI.dispatch(getEntities({}));
      return result;
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const errorMessage = 'We apologize, but you can add only one news every 24 hours.';
        return thunkAPI.rejectWithValue(errorMessage);
      }
      return thunkAPI.rejectWithValue('An error occurred while making the request.');
    }
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'newsFeed/update_entity',
  async (entity: INewsFeed, thunkAPI) => {
    const result = await axios.put<INewsFeed>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'newsFeed/partial_update_entity',
  async (entity: INewsFeed, thunkAPI) => {
    const result = await axios.patch<INewsFeed>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'newsFeed/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<INewsFeed>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);
export const deleteNewsFeedEntity = createAsyncThunk(
  'newsFeed/delete_entity_newsfeed',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<INewsFeed>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const NewsFeedSlice = createEntitySlice({
  name: 'newsFeed',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      // .addCase(getEntityNewsFeed.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.entity = action.payload.data;
      // })
      .addCase(getEntityNewsFeed.fulfilled, (state, action) => {
        const { data, headers } = action.payload;

        state.loading = false;
        state.entity = data; // Assuming that data is an array of inspirations
        state.totalItems = parseInt(headers['x-total-count'], 10);
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(deleteNewsFeedEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, createEntitynewsFeed, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntityNewsFeed), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, createEntitynewsFeed, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = NewsFeedSlice.actions;

// Reducer
export default NewsFeedSlice.reducer;
